import { useState, useRef, createRef } from 'react';
import { uploadBlob } from '../../services/blobUpload';
import { updatePlacePhotos } from '../../services/api';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { ErrorBanner } from '../UI/ErrorBanner';

const SLOT_COUNT = 5;
const PHOTO_KEYS = ['PhotoURL1', 'PhotoURL2', 'PhotoURL3', 'PhotoURL4', 'PhotoURL5'] as const;

interface PlacePhotoManagerProps {
  placeId: string;
  photoURLs: (string | null)[];
  onPhotosChanged: (photos: Record<string, string | null>) => void;
}

export function PlacePhotoManager({ placeId, photoURLs, onPhotosChanged }: PlacePhotoManagerProps) {
  const [urls, setUrls] = useState(() => {
    const arr = [...photoURLs];
    while (arr.length < SLOT_COUNT) arr.push(null);
    return arr;
  });
  const [uploading, setUploading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef(Array.from({ length: SLOT_COUNT }, () => createRef<HTMLInputElement>()));

  const save = async (next: (string | null)[]) => {
    setUrls(next);
    const payload = {
      photoURL1: next[0], photoURL2: next[1], photoURL3: next[2],
      photoURL4: next[3], photoURL5: next[4],
    };
    try {
      await updatePlacePhotos(placeId, payload);
      const update: Record<string, string | null> = {};
      PHOTO_KEYS.forEach((k, i) => { update[k] = next[i]; });
      onPhotosChanged(update);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleFile = async (file: File, slot: number) => {
    setError(null);
    setUploading(slot);
    try {
      const blobUrl = await uploadBlob(file, placeId);
      const next = [...urls];
      next[slot] = blobUrl;
      await save(next);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(null);
    }
  };

  const handleRemove = async (slot: number) => {
    const next = [...urls];
    next[slot] = null;
    await save(next);
  };

  const handleDrop = (e: React.DragEvent, slot: number) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file, slot);
  };

  // Layout: first row 2 large, second row 3 smaller
  const topRow = urls.slice(0, 2);
  const bottomRow = urls.slice(2, SLOT_COUNT);

  const renderSlot = (url: string | null, i: number, height: number) => (
    <div
      key={i}
      onDrop={(e) => handleDrop(e, i)}
      onDragOver={(e) => e.preventDefault()}
      onClick={() => !url && inputRefs.current[i].current?.click()}
      style={{
        flex: 1,
        height,
        border: url ? '1px solid #e0e0e0' : '2px dashed #90caf9',
        borderRadius: 8,
        overflow: 'hidden',
        position: 'relative',
        cursor: url ? 'default' : 'pointer',
        background: url ? 'transparent' : '#f3f8ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {uploading === i ? (
        <LoadingSpinner size={20} />
      ) : url ? (
        <>
          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <button
            onClick={(e) => { e.stopPropagation(); handleRemove(i); }}
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 22,
              height: 22,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </>
      ) : (
        <span style={{ color: '#90caf9', fontSize: 24, fontWeight: 300 }}>+</span>
      )}
      <input
        ref={inputRefs.current[i]}
        type="file"
        accept="image/*,.heic,.heif"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f, i); e.target.value = ''; }}
      />
    </div>
  );

  return (
    <div style={{ padding: '8px 16px 4px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#616161', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        Place Photos
      </div>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        {topRow.map((url, i) => renderSlot(url, i, 120))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {bottomRow.map((url, i) => renderSlot(url, i + 2, 90))}
      </div>
    </div>
  );
}
